import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import Breadcrumb from '@/components/layout/Breadcrumb'
import AudioPlayer from '@/components/quran/AudioPlayer'
import ReadingView from '@/components/quran/ReadingView'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Card from '@/components/ui/Card'
import { quranApi, type SurahDetail } from '@/services/quranApiService'
import { Headphones } from 'lucide-react'

export default function QuranListenPage() {
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const surahParam = searchParams.get('surah')
  const reciterParam = searchParams.get('reciter')
  
  const [selectedSurahNumber, setSelectedSurahNumber] = useState<number>(
    surahParam ? parseInt(surahParam, 10) : 1
  )
  const [initialReciterId, setInitialReciterId] = useState<string | null>(
    reciterParam || localStorage.getItem('selectedReciterId')
  )
  const [surah, setSurah] = useState<SurahDetail | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [allSurahs, setAllSurahs] = useState<Array<{ number: number; name: string }>>([])
  const [highlightedAyah, setHighlightedAyah] = useState<number | undefined>()
  const [highlightedWord, setHighlightedWord] = useState<number | undefined>()

  // Fetch all surahs for selector
  useEffect(() => {
    const fetchSurahs = async () => {
      try {
        const data = await quranApi.getSurahs()
        setAllSurahs(data.map(s => ({ number: s.number, name: s.name })))
      } catch (err) {
        console.error('Failed to fetch surahs list:', err)
      }
    }
    fetchSurahs()
  }, [])

  // Fetch selected surah
  useEffect(() => {
    const fetchSurah = async () => {
      if (!selectedSurahNumber) return

      try {
        setIsLoading(true)
        setError(null)
        const lang = i18n.language.split('-')[0] || 'en'
        const data = await quranApi.getSurah(selectedSurahNumber, lang)
        setSurah(data)
        localStorage.setItem('lastReadSurahNumber', selectedSurahNumber.toString())
      } catch (err) {
        console.error('Failed to fetch surah:', err)
        setError('فشل تحميل بيانات السورة')
      } finally {
        setIsLoading(false)
      }
    }
    fetchSurah()
  }, [selectedSurahNumber, i18n.language])

  // Update URL when surah or reciter changes
  useEffect(() => {
    const params: Record<string, string> = { surah: selectedSurahNumber.toString() }
    if (initialReciterId) {
      params.reciter = initialReciterId
    }
    setSearchParams(params)
  }, [selectedSurahNumber, initialReciterId, setSearchParams])

  const handleSurahChange = (surahNumber: number) => {
    setSelectedSurahNumber(surahNumber)
    setHighlightedAyah(undefined)
    setHighlightedWord(undefined)
  }

  const handleWordHighlight = (ayahNumber: number, wordIndex: number) => {
    setHighlightedAyah(ayahNumber)
    setHighlightedWord(wordIndex)
    
    // Scroll to highlighted ayah
    const ayahElement = document.getElementById(`ayah-${ayahNumber}`)
    if (ayahElement) {
      ayahElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleAyahChange = (ayahNumber: number) => {
    setHighlightedAyah(ayahNumber)
    setHighlightedWord(undefined)
  }

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('quran.index', 'فهرس القرآن'), to: '/quran/index' },
    { label: t('quran.listen', 'الاستماع للقرآن') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('quran.listen', 'الاستماع للقرآن')} - سكينة</title>
        <meta name="description" content={t('quran.listenDescription', 'استمع لتلاوات القرآن الكريم من أشهر القراء')} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Headphones className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('quran.listen', 'الاستماع للقرآن')}
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {t('quran.listenSubtitle', 'استمتع بتلاوات القرآن الكريم من أشهر القراء مع إمكانية متابعة الكلمات')}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error || !surah ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
            {error || 'فشل تحميل بيانات السورة'}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Audio Player and Controls - Top Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Surah Selector - Left */}
              <div className="lg:col-span-1">
                <Card className="p-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    اختر السورة:
                  </label>
                  <select
                    value={selectedSurahNumber}
                    onChange={(e) => handleSurahChange(parseInt(e.target.value, 10))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    {allSurahs.map((s) => (
                      <option key={s.number} value={s.number}>
                        {s.number}. {s.name}
                      </option>
                    ))}
                  </select>
                </Card>
              </div>

              {/* Audio Player - Right (2/3 width) */}
              <div className="lg:col-span-2">
                <AudioPlayer
                  surahNumber={surah.number}
                  ayahs={surah.ayahsList.map((a) => ({
                    number: a.number,
                    text: a.arabicText,
                  }))}
                  onWordHighlight={handleWordHighlight}
                  onAyahChange={handleAyahChange}
                  initialReciterId={initialReciterId}
                />
              </div>
            </div>

            {/* Reading View with Highlighting - Below Player */}
            <Card className="p-6">
              <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
                  {surah.name}
                </h2>
                {surah.nameEn && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-1">
                    {surah.nameEn}
                  </p>
                )}
              </div>
              <ReadingView
                ayahs={surah.ayahsList.map((a) => ({
                  number: a.number,
                  text: a.arabicText,
                }))}
                highlightedAyah={highlightedAyah}
                highlightedWord={highlightedWord}
              />
            </Card>
          </div>
        )}
      </div>
    </>
  )
}

